import { Component, type ErrorInfo, type ReactNode } from 'react'

import { Icon } from '@/icons/Icon'

/**
 * A screen that throws shows what happened, not a white page.
 *
 * React unmounts the whole tree when a render throws, so one undefined field
 * took the entire application down to a blank page - with the reason only in
 * the console, where nobody using the app will look. The boundary keeps the
 * failure on the screen it belongs to and offers the two things that actually
 * help: reload, or go to the dashboard.
 */
interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Still logged: the console trace is what a developer needs.
    console.error('Screen failed to render', error, info.componentStack)
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="ix-page" style={{ padding: '22px' }}>
        <div className="ix-alert ix-alert--error" role="alert">
          <Icon name="alert-triangle" size={18} />
          <div>
            <span className="ix-alert__title">This screen hit an error</span>
            <span>
              {error.message || 'Something went wrong while drawing this page.'} Reloading usually clears
              it; if it keeps happening, this message is the one to report.
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="ix-btn ix-btn--primary" type="button" onClick={() => window.location.reload()}>
            Reload this page
          </button>
          <a className="ix-btn ix-btn--secondary" href="/dashboard">
            Go to the dashboard
          </a>
        </div>
      </div>
    )
  }
}

export default ErrorBoundary
